import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  ArrowLeft,
  Clock,
  CheckCircle,
  Shield,
  Star,
  Users,
  Calendar,
  Phone,
  MessageSquare,
  MapPin,
  Home,
  Building,
  Droplets,
  Wind,
  Sun,
  Zap,
  Sparkles,
  Square,
  Bed,
  Award,
  Leaf,
  Target,
  Truck,
  ShieldCheck,
  Thermometer,
  Heart,
  Cloud,
  RefreshCw,
  MessageCircle,
  X,
  User,
  Mail,
  Map,
  Check,
  PhoneCall,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Shield as ShieldIcon,
  Clock as ClockIcon,
  Truck as TruckIcon,
  Car, // Added Car icon for car seats cleaning
} from "lucide-react";

const ServiceDetail = () => {
  const { serviceSlug } = useParams();
  const navigate = useNavigate();
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  // FAQ Toggle Handler
  const toggleFAQ = (index) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  // Helper function to get schema for each service
  const getServiceSchema = (slug, service) => {
    const baseSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "provider": {
        "@type": "CleaningService",
        "name": "Knight Cleanings",
        "telephone": "+61420499661",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "11 Warath Drive, Dandenong North",
          "addressLocality": "Melbourne",
          "addressRegion": "VIC",
          "postalCode": "3175",
          "addressCountry": "AU"
        },
        "logo": "https://knightcleanings.com.au/logo.png",
        "image": "https://knightcleanings.com.au/images/storefront.jpg",
        "priceRange": "$$",
        "areaServed": [
          { "@type": "City", "name": "Melbourne" },
          { "@type": "City", "name": "Dandenong" },
          { "@type": "City", "name": "Hallam" },
          { "@type": "City", "name": "Hampton Park" },
          { "@type": "City", "name": "Clayton" },
          { "@type": "City", "name": "Keysborough" },
          { "@type": "City", "name": "Glen Waverley" },
          { "@type": "City", "name": "Mount Waverley" },
          { "@type": "City", "name": "Noble Park" },
          { "@type": "City", "name": "Lynbrook" },
          { "@type": "City", "name": "Doveton" },
          { "@type": "AdministrativeArea", "name": "Mornington Peninsula" }
        ]
      },
      "areaServed": [
        { "@type": "City", "name": "Melbourne" },
        { "@type": "City", "name": "Dandenong" },
        { "@type": "City", "name": "Hallam" },
        { "@type": "City", "name": "Hampton Park" },
        { "@type": "City", "name": "Clayton" },
        { "@type": "City", "name": "Keysborough" },
        { "@type": "City", "name": "Glen Waverley" },
        { "@type": "City", "name": "Mount Waverley" },
        { "@type": "City", "name": "Noble Park" },
        { "@type": "City", "name": "Lynbrook" },
        { "@type": "City", "name": "Doveton" },
        { "@type": "AdministrativeArea", "name": "Mornington Peninsula" }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": service.title,
        "itemListElement": service.features.map((feature, index) => ({
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": feature,
            "description": `${feature} service included in ${service.title}`
          }
        }))
      }
    };

    // Service-specific configurations
    const serviceSchemas = {
      "residential-cleaning": {
        ...baseSchema,
        "name": "Residential Cleaning Services in Melbourne",
        "url": "https://knightcleanings.com.au/services/residential-cleaning",
        "description": service.description,
        "serviceType": "Residential Cleaning",
        "category": "Home Cleaning",
        "termsOfService": "https://knightcleanings.com.au/terms",
        "mainEntityOfPage": "https://knightcleanings.com.au/services/residential-cleaning"
      },
      "commercial-cleaning": {
        ...baseSchema,
        "name": "Commercial Cleaning Services in Melbourne",
        "url": "https://knightcleanings.com.au/services/commercial-cleaning",
        "description": service.description,
        "serviceType": "Commercial Cleaning",
        "category": "Business Cleaning",
        "termsOfService": "https://knightcleanings.com.au/terms",
        "mainEntityOfPage": "https://knightcleanings.com.au/services/commercial-cleaning"
      },
      "end-of-lease-cleaning": {
        ...baseSchema,
        "name": "End of Lease Cleaning in Melbourne",
        "url": "https://knightcleanings.com.au/services/end-of-lease-cleaning",
        "description": service.description,
        "serviceType": "End of Lease Cleaning",
        "category": "Bond Cleaning",
        "termsOfService": "https://knightcleanings.com.au/terms",
        "mainEntityOfPage": "https://knightcleanings.com.au/services/end-of-lease-cleaning"
      },
      "carpet-upholstery-cleaning": {
        ...baseSchema,
        "name": "Carpet and Upholstery Cleaning Services in Melbourne",
        "url": "https://knightcleanings.com.au/services/carpet-upholstery-cleaning",
        "description": service.description,
        "serviceType": "Carpet & Upholstery Cleaning",
        "category": "Fabric Care",
        "termsOfService": "https://knightcleanings.com.au/terms",
        "mainEntityOfPage": "https://knightcleanings.com.au/services/carpet-upholstery-cleaning"
      },
      "duct-vent-cleaning": {
        ...baseSchema,
        "name": "Duct and Vent Cleaning Services in Melbourne",
        "url": "https://knightcleanings.com.au/services/duct-vent-cleaning",
        "description": service.description,
        "serviceType": "Duct & Vent Cleaning",
        "category": "HVAC Cleaning",
        "termsOfService": "https://knightcleanings.com.au/terms",
        "mainEntityOfPage": "https://knightcleanings.com.au/services/duct-vent-cleaning"
      },
      "window-gutter-cleaning": {
        ...baseSchema,
        "name": "Window and Gutter Cleaning Services in Melbourne",
        "url": "https://knightcleanings.com.au/services/window-gutter-cleaning",
        "description": service.description,
        "serviceType": "Window & Gutter Cleaning",
        "category": "Exterior Cleaning",
        "termsOfService": "https://knightcleanings.com.au/terms",
        "mainEntityOfPage": "https://knightcleanings.com.au/services/window-gutter-cleaning"
      },
      "emergency-restoration": {
        ...baseSchema,
        "name": "Emergency Restoration Services in Melbourne",
        "url": "https://knightcleanings.com.au/services/emergency-restoration",
        "description": service.description,
        "serviceType": "Emergency Restoration",
        "category": "Disaster Recovery",
        "termsOfService": "https://knightcleanings.com.au/terms",
        "mainEntityOfPage": "https://knightcleanings.com.au/services/emergency-restoration"
      },
      "deep-cleaning": {
        ...baseSchema,
        "name": "Deep Cleaning Services in Melbourne",
        "url": "https://knightcleanings.com.au/services/deep-cleaning",
        "description": service.description,
        "serviceType": "Deep Cleaning",
        "category": "Intensive Cleaning",
        "termsOfService": "https://knightcleanings.com.au/terms",
        "mainEntityOfPage": "https://knightcleanings.com.au/services/deep-cleaning"
      },
      "rug-cleaning": {
        ...baseSchema,
        "name": "Rug, Mattress and Curtain Cleaning Services in Melbourne",
        "url": "https://knightcleanings.com.au/services/rug-cleaning",
        "description": service.description,
        "serviceType": "Rug & Mattress Cleaning",
        "category": "Specialty Cleaning",
        "termsOfService": "https://knightcleanings.com.au/terms",
        "mainEntityOfPage": "https://knightcleanings.com.au/services/rug-cleaning"
      },
      "mattress-cleaning": {
        ...baseSchema,
        "name": "Mattress Cleaning Services in Melbourne",
        "url": "https://knightcleanings.com.au/services/mattress-cleaning",
        "description": service.description,
        "serviceType": "Mattress Cleaning",
        "category": "Sleep Hygiene",
        "termsOfService": "https://knightcleanings.com.au/terms",
        "mainEntityOfPage": "https://knightcleanings.com.au/services/mattress-cleaning"
      },
      "rugs-deep-cleaning": {
        ...baseSchema,
        "name": "Deep Rug Cleaning Services in Melbourne",
        "url": "https://knightcleanings.com.au/services/rugs-deep-cleaning",
        "description": service.description,
        "serviceType": "Deep Rug Cleaning",
        "category": "Rug Restoration",
        "termsOfService": "https://knightcleanings.com.au/terms",
        "mainEntityOfPage": "https://knightcleanings.com.au/services/rugs-deep-cleaning"
      },
      "curtain-cleaning": {
        ...baseSchema,
        "name": "Curtain Cleaning Services in Melbourne",
        "url": "https://knightcleanings.com.au/services/curtain-cleaning",
        "description": service.description,
        "serviceType": "Curtain Cleaning",
        "category": "Window Treatment Care",
        "termsOfService": "https://knightcleanings.com.au/terms",
        "mainEntityOfPage": "https://knightcleanings.com.au/services/curtain-cleaning"
      },
      // NEW: Car Seats Cleaning Schema
      "car-seats-cleaning": {
        ...baseSchema,
        "name": "Car Seats Cleaning Services in Melbourne",
        "url": "https://knightcleanings.com.au/services/car-seats-cleaning",
        "description": service.description,
        "serviceType": "Car Seats Cleaning",
        "category": "Automotive Cleaning",
        "termsOfService": "https://knightcleanings.com.au/terms",
        "mainEntityOfPage": "https://knightcleanings.com.au/services/car-seats-cleaning"
      }
    };

    return serviceSchemas[slug] || baseSchema;
  };

  // Complete services data with all 13 services
  const servicesData = {
    "residential-cleaning": {
      id: 1,
      icon: Home,
      title: "Residential Cleaning Services in Melbourne VIC",
      description: "Professional residential cleaning services in Melbourne VIC. We provide reliable, detailed home cleaning for apartments, houses, and villas.",
      features: ["Deep Cleaning of All Rooms", "Kitchen Sanitization & Appliance Cleaning", "Bathroom Disinfection & Grout Cleaning", "Dusting of All Surfaces & Furniture", "Vacuuming & Mopping All Floors", "Window Sills & Interior Glass", "Baseboard Cleaning", "Light Switch & Door Handle Sanitization"],
      image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=2070&auto=format&fit=crop",
      color: "from-blue-500 to-indigo-600",
      stats: { time: "2-4 hours", coverage: "All Rooms", guarantee: "100% Satisfaction", price: "Starting from $129" },
      popular: true,
      process: ["Initial Consultation & Custom Plan", "Pre-treatment of Stubborn Areas", "Top-to-Bottom Dusting", "Kitchen Deep Clean & Sanitization", "Bathroom Disinfection & Scrubbing", "Floor Care & Vacuuming", "Final Quality Inspection", "Client Walkthrough & Approval"],
      areasServed: ["Melbourne CBD", "Dandenong", "Hallam", "Hampton Park", "Clayton", "Keysborough", "Glen Waverley", "Mount Waverley", "Noble Park", "Lynbrook", "Doveton", "Surrounding Suburbs"],
      pricing: [
        { name: "Studio/1 Bedroom", price: "$129", details: "Perfect for apartments" },
        { name: "2 Bedroom Home", price: "$199", details: "Includes living areas" },
        { name: "3 Bedroom Home", price: "$249", details: "Complete home clean" },
        { name: "4+ Bedroom Home", price: "Custom Quote", details: "Large homes & properties" },
      ],
      benefits: ["Healthier living environment", "Reduced allergens & dust", "Extended furniture life", "More free time for you", "Professional results", "Consistent cleaning schedule"],
      equipment: ["HEPA-filter vacuums", "Microfiber cleaning cloths", "Eco-friendly cleaning solutions", "Extension poles for high areas", "Professional-grade mops", "UV-C sanitization wands"],
    },
    "commercial-cleaning": {
      id: 2,
      icon: Building,
      title: "Commercial Cleaning Services in Melbourne VIC",
      description: "Trusted commercial cleaning services in Melbourne VIC for offices, retail spaces, and facilities. Flexible plans with spotless results.",
      longDescription: `Our commercial cleaning services are designed to maintain a pristine, healthy work environment that enhances productivity and impresses clients. We understand that each business has unique cleaning needs, which is why we create customized cleaning plans tailored to your schedule, budget, and specific requirements.`,
      features: ["Daily/Weekly Office Cleaning", "Restroom Sanitization & Restocking", "Kitchen & Breakroom Cleaning", "Floor Care & Maintenance", "Window & Glass Cleaning", "Waste Management & Recycling", "High-Dusting & Vent Cleaning", "Disinfection of High-Touch Areas"],
      image: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?q=80&w=2070&auto=format&fit=crop",
      color: "from-emerald-500 to-teal-600",
      stats: { time: "Custom Hours", coverage: "Entire Facility", guarantee: "Insurance Backed", price: "Custom Pricing" },
      process: ["Facility Assessment & Planning", "Customized Cleaning Protocol", "After-Hours Cleaning Service", "Daily Maintenance Tasks", "Weekly Deep Cleaning", "Monthly Specialized Services", "Quality Control Checks", "Monthly Reporting"],
      areasServed: ["Melbourne CBD Offices", "Dandenong Business Parks", "Clayton Industrial Area", "Glen Waverley Commercial", "Keysborough Warehouses", "All Melbourne Business Districts", "Industrial Zones", "Retail Spaces"],
      pricing: [
        { name: "Small Office", price: "$299/week", details: "Up to 1000 sq ft" },
        { name: "Medium Office", price: "$499/week", details: "1000-3000 sq ft" },
        { name: "Large Office", price: "$899/week", details: "3000-5000 sq ft" },
        { name: "Industrial/Custom", price: "Custom Quote", details: "Tailored solutions" },
      ],
      benefits: ["Increased employee productivity", "Enhanced professional image", "Reduced sick days", "Extended asset life", "Compliance with regulations", "Customizable schedules"],
      equipment: ["Industrial floor scrubbers", "Commercial vacuum systems", "Electrostatic sprayers", "High-reach cleaning tools", "Pressure washers", "Commercial-grade chemicals"],
    },
    "carpet-upholstery-cleaning": {
      id: 3,
      icon: Droplets,
      title: "Carpet and Upholstery Cleaning in Melbourne VIC",
      description: "Expert carpet and upholstery cleaning services in Melbourne VIC. Remove stains, dirt, and allergens with safe, effective methods.",
      longDescription: `Our professional carpet and upholstery cleaning service uses state-of-the-art steam cleaning technology and eco-friendly solutions to rejuvenate your fabrics. We specialize in removing deep-seated dirt, stubborn stains, pet odors, and allergens that regular vacuuming can't reach.`,
      features: ["Hot Water Extraction Cleaning", "Stain Removal Guarantee", "Pet Odor & Stain Treatment", "Allergen Reduction (Dust Mites, Pollen)", "Antibacterial Sanitization", "Quick Drying Technology", "Fabric Protection Application", "Spot Cleaning & Repair"],
      image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?q=80&w=2070&auto=format&fit=crop",
      color: "from-amber-500 to-orange-600",
      stats: { time: "1-3 hours", coverage: "Per Room", guarantee: "Stain-Free Promise", price: "Starting from $79" },
      popular: false,
      process: ["Pre-inspection & Fabric Testing", "Pre-treatment of Stains & Heavy Soil", "Hot Water Extraction Cleaning", "Steam Cleaning & Sanitization", "Spot Treatment & Stain Removal", "Antibacterial Application", "Rapid Airflow Drying", "Final Inspection & Grooming"],
      areasServed: ["Melbourne CBD", "Dandenong", "Hallam", "Hampton Park", "Clayton", "Keysborough", "Noble Park", "Glen Waverley", "Mount Waverley", "Lynbrook", "Doveton"],
      pricing: [
        { name: "Per Room", price: "$79 - $129", details: "Based on size & condition" },
        { name: "Large Living Area", price: "$149", details: "Open plan spaces" },
        { name: "Sofa Cleaning", price: "$99 - $199", details: "2-3 seater" },
        { name: "Chair Cleaning", price: "$49 - $79", details: "Single chairs" },
        { name: "Staircase", price: "$5/step", details: "Carpeted stairs" },
      ],
      benefits: ["Extended carpet life", "Improved indoor air quality", "Removal of allergens & bacteria", "Restoration of original colors", "Elimination of odors", "Protection against future stains"],
      equipment: ["Truck-mounted steam cleaners", "Portable extraction units", "Rotary cleaning machines", "Commercial-grade stain removers", "Air movers for drying", "Protective coating applicators"],
    },
    "duct-vent-cleaning": {
      id: 4,
      icon: Wind,
      title: "Duct and Vent Cleaning Services in Melbourne VIC",
      description: "Professional duct and vent cleaning services in Melbourne VIC to improve airflow and indoor air quality in homes and businesses.",
      longDescription: `Our professional duct and vent cleaning service removes accumulated dust, debris, mold, and allergens from your HVAC system. Over time, these contaminants circulate through your home or office, affecting air quality and potentially causing health issues.`,
      features: ["Complete HVAC System Cleaning", "Mold & Mildew Removal", "Allergen Reduction", "Improved Airflow & Efficiency", "Sanitization & Deodorization", "Filter Replacement Service", "Before/After Video Documentation", "Air Quality Testing Available"],
      image: "https://images.unsplash.com/photo-1558618883-8c6fc9c5f751?q=80&w=2070&auto=format&fit=crop",
      color: "from-purple-500 to-violet-600",
      stats: { time: "3-6 hours", coverage: "Full System", guarantee: "Air Quality Test", price: "Starting from $299" },
      process: ["System Inspection & Assessment", "Protection of Work Area", "Negative Air Pressure Setup", "Duct & Vent Agitation Cleaning", "Air Handler & Coil Cleaning", "Sanitization & Deodorization", "Filter Replacement", "Final System Check"],
      areasServed: ["All Melbourne Suburbs", "Dandenong", "Clayton", "Keysborough", "Glen Waverley", "Mornington Peninsula", "Residential & Commercial", "New Construction & Renovations"],
      pricing: [
        { name: "Standard Home", price: "$299 - $499", details: "Up to 10 vents" },
        { name: "Large Home", price: "$599 - $899", details: "10-20 vents" },
        { name: "Commercial", price: "Custom Quote", details: "Office buildings" },
        { name: "Add-on: Dryer Vent", price: "$99", details: "Fire hazard prevention" },
      ],
      benefits: ["Improved respiratory health", "Reduced allergy symptoms", "Lower energy bills", "Extended HVAC lifespan", "Elimination of musty odors", "Reduced dust accumulation"],
      equipment: ["Negative air machines", "Rotary brush systems", "Video inspection cameras", "HEPA filtration units", "Air compressors", "Sanitization foggers"],
    },
    "end-of-lease-cleaning": {
      id: 5,
      icon: Shield,
      title: "End of Lease Cleaning Services in Melbourne VIC",
      description: "Reliable end of lease cleaning services in Melbourne VIC meeting real estate standards. Helping tenants secure full bond returns.",
      longDescription: `Our bond cleaning service is specifically designed to meet and exceed real estate agency standards. We provide meticulous cleaning of every corner of your property to ensure you receive your full bond refund. Our comprehensive checklist covers over 100 points of cleaning.`,
      features: ["Bond Back Guarantee", "Real Estate Standard Cleaning", "Full Property Deep Clean", "Carpet Steam Cleaning Included", "Window Cleaning (Interior)", "Oven & Appliance Deep Clean", "Wall Wiping & Mark Removal", "Professional Inspection Report"],
      image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=2073&auto=format&fit=crop",
      color: "from-rose-500 to-pink-600",
      stats: { time: "4-8 hours", coverage: "Entire Property", guarantee: "Bond Back", price: "Starting from $299" },
      popular: true,
      process: ["Property Walkthrough & Checklist", "Kitchen Deep Clean & Degreasing", "Bathroom Sanitization & Grout Cleaning", "Carpet & Floor Treatment", "Window & Glass Cleaning", "Wall Wiping & Touch-ups", "Final Agency-Standard Inspection", "Bond Back Guarantee Certificate"],
      areasServed: ["All Melbourne Suburbs", "Dandenong", "Hallam", "Hampton Park", "Clayton", "Keysborough", "Noble Park", "Glen Waverley", "Mount Waverley", "Lynbrook", "Doveton"],
      pricing: [
        { name: "1 Bedroom Unit", price: "$299", details: "Apartments & units" },
        { name: "2 Bedroom Apartment", price: "$399", details: "Standard cleaning" },
        { name: "3 Bedroom House", price: "$499", details: "Complete house clean" },
        { name: "4+ Bedroom House", price: "Custom Quote", details: "Large properties" },
      ],
      benefits: ["Full bond refund guarantee", "Stress-free moving process", "Professional standards", "Time-saving solution", "Real estate approved", "Comprehensive checklist"],
      equipment: ["Professional steam cleaners", "Window cleaning kits", "Grout cleaning tools", "Oven degreasers", "Wall washing systems", "Industrial vacuum cleaners"],
    },
    "window-gutter-cleaning": {
      id: 6,
      icon: Sun,
      title: "Window and Gutter Cleaning Services Melbourne VIC",
      description: "Professional window and gutter cleaning services in Melbourne VIC delivering streak-free windows and debris-free gutters.",
      longDescription: `Our professional window and gutter cleaning service enhances your property's appearance while protecting its structural integrity. Clean windows allow more natural light in and improve your view, while clean gutters prevent water damage, foundation issues, and pest infestations.`,
      features: ["Interior & Exterior Window Cleaning", "Gutter Clearing & Downpipe Flushing", "Screen Cleaning & Repair", "Hard Water Stain Removal", "Gutter Guard Installation", "Roof & Gutter Inspection", "Pressure Washing Available", "Squeegee & Pure Water Systems"],
      image: "https://images.unsplash.com/photo-1628083333913-f2cc3d1b7f9c?q=80&w=2070&auto=format&fit=crop",
      color: "from-cyan-500 to-blue-600",
      stats: { time: "1-2 hours", coverage: "All Windows", guarantee: "Streak-Free", price: "Starting from $149" },
      process: ["Property Safety Assessment", "Pure Water System Setup", "Window Cleaning & Drying", "Gutter Debris Removal", "Downpipe Flushing", "Screen Cleaning", "Final Inspection & Polish", "Water Flow Testing"],
      areasServed: ["Mornington Peninsula", "Mornington", "Rye", "Rosebud", "Dandenong", "Glen Waverley", "Clayton", "Keysborough", "All Melbourne Suburbs", "Residential & Commercial"],
      pricing: [
        { name: "Standard Home", price: "$149 - $249", details: "Windows & gutters" },
        { name: "Two-Story Home", price: "$249 - $399", details: "With extension ladders" },
        { name: "Commercial", price: "Custom Quote", details: "Office buildings" },
        { name: "Gutter Guard Install", price: "$15/m", details: "Debris prevention" },
      ],
      benefits: ["Enhanced curb appeal", "Prevent water damage", "Extend gutter lifespan", "Improved natural lighting", "Pest prevention", "Regular maintenance plan"],
      equipment: ["Pure water filtration systems", "Water-fed pole systems", "Professional squeegees", "Gutter vacuum systems", "Extension ladders & platforms", "Pressure washers"],
    },
    "emergency-restoration": {
      id: 7,
      icon: Zap,
      title: "Emergency Restoration Services in Melbourne VIC",
      description: "Emergency restoration services in Melbourne VIC for water, fire, and property damage. Fast response when you need it most.",
      longDescription: `Our emergency restoration service provides immediate response to water damage, flooding, fire damage, and other disasters. We're available 24/7 to help mitigate damage and begin the restoration process quickly. Time is critical in emergency situations, and our rapid response team is always ready.`,
      features: ["24/7 Emergency Response", "Water Extraction & Drying", "Mold Remediation & Prevention", "Fire & Smoke Damage Cleanup", "Odor Removal & Deodorization", "Insurance Claim Assistance", "Structural Drying & Monitoring", "Complete Reconstruction Services"],
      image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=2073&auto=format&fit=crop",
      color: "from-red-500 to-orange-600",
      stats: { time: "Immediate", coverage: "Emergency Response", guarantee: "Insurance Ready", price: "Emergency Call-out" },
      process: ["Emergency Dispatch & Assessment", "Water Extraction & Removal", "Structural Drying Setup", "Mold Prevention Treatment", "Cleaning & Sanitization", "Odor Removal & Deodorization", "Monitoring & Documentation", "Reconstruction & Restoration"],
      areasServed: ["24/7 Melbourne Wide", "Dandenong Emergency", "Clayton & Keysborough", "Glen Waverley", "Mornington Peninsula", "All Suburbs Covered", "Residential & Commercial", "Industrial Properties"],
      pricing: [
        { name: "Emergency Call-out", price: "$299", details: "Includes assessment" },
        { name: "Water Extraction", price: "$99/hour", details: "Per technician" },
        { name: "Structural Drying", price: "$149/day", details: "Equipment rental" },
        { name: "Insurance Claims", price: "No Cost", details: "We work with insurers" },
      ],
      benefits: ["24/7 emergency response", "Prevent secondary damage", "Insurance claim assistance", "Professional equipment", "Certified technicians", "Complete restoration"],
      equipment: ["Industrial water extractors", "Commercial dehumidifiers", "Air movers & fans", "Moisture detection meters", "Thermal imaging cameras", "Ozone generators"],
    },
    "deep-cleaning": {
      id: 8,
      icon: Sparkles,
      title: "Deep Cleaning Services in Melbourne VIC",
      description: "Comprehensive deep cleaning services in Melbourne VIC targeting hidden dirt, bacteria, and buildup for total hygiene.",
      longDescription: `Our deep cleaning service goes beyond regular maintenance to provide intensive, detailed cleaning of your entire home or office. This service is perfect for spring cleaning, post-renovation cleanup, pre-event preparation, or when you need that extra level of cleanliness.`,
      features: ["Detailed Sanitization of All Areas", "Grout & Tile Deep Cleaning", "Appliance Interior Cleaning", "Light Fixture & Ceiling Fan Cleaning", "Behind & Under Furniture", "Window Track & Sill Cleaning", "Fixture Polishing & Detailing", "Wall Washing & Mark Removal"],
      image: "https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?q=80&w=2070&auto=format&fit=crop",
      color: "from-indigo-500 to-purple-600",
      stats: { time: "5-8 hours", coverage: "Complete Home", guarantee: "Deep Clean Guarantee", price: "Starting from $349" },
      process: ["Comprehensive Property Assessment", "Moving & Protecting Furniture", "Top-to-Bottom Detailed Cleaning", "Appliance & Fixture Deep Clean", "Grout & Tile Restoration", "Final Polish & Detailing", "Quality Control Inspection", "Client Satisfaction Check"],
      areasServed: ["All Melbourne Suburbs", "Dandenong", "Hallam", "Hampton Park", "Clayton", "Keysborough", "Glen Waverley", "Mount Waverley", "Noble Park", "Residential & Commercial"],
      pricing: [
        { name: "2 Bedroom Home", price: "$349", details: "Standard deep clean" },
        { name: "3 Bedroom Home", price: "$449", details: "Comprehensive service" },
        { name: "4+ Bedroom Home", price: "$549+", details: "Large property" },
        { name: "Commercial Deep Clean", price: "Custom Quote", details: "Office spaces" },
      ],
      benefits: ["Eliminate hidden dirt & grime", "Improve indoor air quality", "Restore surfaces to like-new", "Sanitize high-touch areas", "Extend property life", "Perfect for special occasions"],
      equipment: ["Steam cleaners & sanitizers", "Grout cleaning tools", "Extension poles & ladders", "Microfiber detailing tools", "Appliance cleaning kits", "UV sanitization lights"],
    },
    "rug-cleaning": {
      id: 9,
      icon: Square,
      title: "Professional Rug Cleaning Services in Melbourne",
      description: "Professional rug cleaning services in Melbourne VIC using gentle methods to remove stains, odors, and embedded dirt.",
      longDescription: `Our professional rug cleaning service specializes in caring for your valuable area rugs and carpets. We understand that different rug materials require different treatments - from delicate silk and wool to durable synthetic fibers.`,
      features: ["Hand Washing for Delicate Rugs", "Specialized Stain Treatment", "Fringe Cleaning & Restoration", "Gentle Drying Process", "Color Protection & Enhancement", "Moth & Pest Prevention", "Antique Rug Specialists", "Pickup & Delivery Service"],
      image: "https://images.unsplash.com/photo-1575428652377-a2d80e2277fc?q=80&w=2070&auto=format&fit=crop",
      color: "from-amber-600 to-yellow-600",
      stats: { time: "3-5 hours", coverage: "Per Rug", guarantee: "Color Protection", price: "Starting from $99" },
      popular: true,
      process: ["Rug Assessment & Fiber Testing", "Pre-treatment & Stain Identification", "Hand Washing & Gentle Agitation", "Specialized Stain Removal", "Rinsing & pH Balancing", "Gentle Spin Extraction", "Controlled Environment Drying", "Final Grooming & Inspection"],
      areasServed: ["Melbourne CBD", "Dandenong", "Hallam", "Hampton Park", "Clayton", "Keysborough", "Glen Waverley", "Mornington Peninsula", "All Melbourne Suburbs", "Pickup Service Available"],
      pricing: [
        { name: "Small Rug", price: "$99", details: "Up to 2x3 meters" },
        { name: "Medium Rug", price: "$149", details: "2x3 to 3x5 meters" },
        { name: "Large Rug", price: "$199", details: "Over 3x5 meters" },
        { name: "Antique/Oriental", price: "Custom Quote", details: "Specialist care" },
        { name: "Pickup & Delivery", price: "$49", details: "Door-to-door service" },
      ],
      benefits: ["Preserve rug value & beauty", "Remove deep-seated dirt", "Eliminate odors & allergens", "Professional stain removal", "Extend rug lifespan", "Expert care for delicate fibers"],
      equipment: ["Professional rug washing tables", "pH-balanced cleaning solutions", "Specialized stain removers", "Gentle extraction systems", "Climate-controlled drying room", "Fringe cleaning tools"],
    },
    "mattress-cleaning": {
      id: 10,
      icon: Bed,
      title: "Mattress Cleaning Services in Melbourne VIC",
      description: "Expert mattress cleaning services in Melbourne VIC removing dust mites, stains, and allergens for a healthier sleep.",
      longDescription: `Our mattress cleaning service provides deep sanitization to create a healthier sleep environment. Over time, mattresses accumulate dust mites, dead skin cells, allergens, and bacteria that can affect your sleep quality and health.`,
      features: ["Allergen & Dust Mite Removal", "Stain Treatment & Removal", "Hospital-Grade Sanitization", "Odor Neutralization", "Bacteria & Germ Elimination", "Quick Drying Technology", "Eco-Friendly Solutions", "Pet Accident Treatment"],
      image: "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?q=80&w=2070&auto=format&fit=crop",
      color: "from-purple-600 to-pink-600",
      stats: { time: "1-2 hours", coverage: "Per Mattress", guarantee: "Allergen Free", price: "Starting from $99" },
      process: ["Mattress Inspection & Testing", "Vacuuming with HEPA Filter", "Pre-treatment of Stains & Spots", "Steam Cleaning & Sanitization", "Stain Removal Treatment", "Deodorizing & Freshness Application", "Rapid Airflow Drying", "Final Quality Assurance"],
      areasServed: ["Mornington Peninsula", "Mornington", "Rye", "Rosebud", "Dandenong", "Frankston", "Glen Waverley", "Clayton", "Keysborough", "All Melbourne Suburbs"],
      pricing: [
        { name: "Single Mattress", price: "$99", details: "Standard cleaning" },
        { name: "Double Mattress", price: "$129", details: "Thorough sanitization" },
        { name: "Queen Mattress", price: "$149", details: "Complete treatment" },
        { name: "King Mattress", price: "$179", details: "Large mattress" },
        { name: "Super King", price: "$199", details: "Extra large" },
      ],
      benefits: ["Improved sleep quality", "Reduced allergy symptoms", "Eliminate bed bugs & mites", "Remove stains & odors", "Extend mattress life", "Healthier sleeping environment"],
      equipment: ["HEPA-filter vacuum systems", "Commercial steam cleaners", "UV-C sanitization lights", "Stain extraction tools", "Air movers for drying", "Eco-friendly sanitizers"],
    },
    "rugs-deep-cleaning": {
      id: 11,
      icon: Square,
      title: "Deep Rug Cleaning Services in Melbourne VIC",
      description: "Deep rug cleaning services in Melbourne VIC designed to eliminate stubborn dirt, bacteria, and odors while protecting fabric.",
      longDescription: `Our rugs deep cleaning service is designed for heavily soiled rugs or those in high-traffic areas that require intensive restoration. We use advanced cleaning techniques and specialized equipment to revive even the most worn-looking rugs.`,
      features: ["Deep Extraction Cleaning", "pH-Balanced Cleaning Solutions", "Mold & Mildew Prevention", "Texture & Pile Restoration", "Heavy Stain Removal", "Traffic Lane Treatment", "Anti-static Treatment", "Soil & Stain Repellent"],
      image: "https://images.unsplash.com/photo-1531835551805-16d864c8d311?q=80&w=2070&auto=format&fit=crop",
      color: "from-teal-600 to-emerald-600",
      stats: { time: "2-4 hours", coverage: "All Rugs", guarantee: "Deep Clean Guarantee", price: "Starting from $149" },
      process: ["Deep Vacuuming & Soil Removal", "Pre-treatment & Conditioner Application", "Rotary Machine Deep Cleaning", "Heavy Stain Treatment", "pH-Balanced Rinsing", "Extraction & Moisture Control", "Pile Lifting & Grooming", "Protective Coating Application"],
      areasServed: ["All Melbourne Suburbs", "Dandenong", "Hallam", "Hampton Park", "Clayton", "Keysborough", "Glen Waverley", "Commercial Properties", "High-Traffic Areas", "Hospitality Industry"],
      pricing: [
        { name: "Residential Deep Clean", price: "$149", details: "Per average rug" },
        { name: "Commercial Rug Cleaning", price: "$199", details: "Heavy traffic areas" },
        { name: "Very Large Rugs", price: "$249+", details: "Over 4x6 meters" },
        { name: "Emergency Spill Cleanup", price: "$99", details: "Immediate treatment" },
      ],
      benefits: ["Restore heavily soiled rugs", "Revive high-traffic areas", "Remove ground-in dirt", "Extend commercial rug life", "Improve appearance", "Professional results"],
      equipment: ["Rotary cleaning machines", "Commercial extractors", "Pile lifters & groomers", "Heavy-duty stain removers", "Moisture meters", "Protective coating systems"],
    },
    "curtain-cleaning": {
      id: 12,
      icon: Square,
      title: "Curtain Cleaning Services in Melbourne VIC",
      description: "Professional curtain cleaning services in Melbourne VIC removing dust, stains, and allergens while preserving fabric quality.",
      longDescription: `Our professional curtain cleaning service safely and effectively cleans all types of window treatments - from delicate sheers to heavy drapes. We understand that curtains accumulate dust, allergens, and odors that regular vacuuming can't remove, affecting your indoor air quality.`,
      features: ["On-Site & Facility Cleaning Options", "Fabric-Safe Cleaning Solutions", "Dust & Allergen Removal", "Stain Treatment & Removal", "Wrinkle Reduction & Pressing", "Odor Elimination", "Color Protection", "Re-hanging Service"],
      image: "https://images.unsplash.com/photo-1582582429416-1c3d26c36b7f?q=80&w=2070&auto=format&fit=crop",
      color: "from-teal-600 to-emerald-600",
      stats: { time: "1-3 hours", coverage: "All Curtain Types", guarantee: "Freshness Guarantee", price: "Starting from $129" },
      process: ["Fabric Assessment & Testing", "Gentle Vacuuming & Dust Removal", "Specialized Cleaning Process", "Stain Treatment & Removal", "Rinsing & pH Balancing", "Gentle Drying & Deodorizing", "Pressing & Wrinkle Removal", "Re-hanging & Final Inspection"],
      areasServed: ["Melbourne CBD", "Dandenong", "Hallam", "Hampton Park", "Clayton", "Keysborough", "Glen Waverley", "Mornington Peninsula", "All Melbourne Suburbs", "Residential & Commercial"],
      pricing: [
        { name: "Per Panel (Standard)", price: "$29", details: "Average size curtains" },
        { name: "Delicate Fabrics", price: "$39/panel", details: "Silk, velvet, etc." },
        { name: "Heavy Drapes", price: "$49/panel", details: "Lined or blackout" },
        { name: "Sheer Curtains", price: "$19/panel", details: "Light fabrics" },
        { name: "Re-hanging Service", price: "$49", details: "Professional installation" },
      ],
      benefits: ["Improved indoor air quality", "Preserve fabric quality", "Remove allergens & dust", "Eliminate odors", "Enhance room appearance", "Extend curtain lifespan"],
      equipment: ["Specialized curtain cleaning machines", "Fabric testing kits", "Gentle cleaning solutions", "Steam cleaning systems", "Professional pressing equipment", "Color protection treatments"],
    },
    // NEW: Car Seats Cleaning Service
    "car-seats-cleaning": {
      id: 13,
      icon: Car,
      title: "Car Seats Cleaning Services in Melbourne VIC",
      description: "Professional car seats cleaning services in Melbourne VIC. Deep clean and sanitize fabric or leather car seats to remove stains, odors, and allergens.",
      longDescription: `Our professional car seats cleaning service provides deep sanitization for your vehicle's upholstery. Whether you have fabric or leather seats, we remove stubborn stains, eliminate odors, and eradicate allergens like dust mites and pet dander. Regular car seat cleaning not only improves your driving experience but also maintains your vehicle's resale value and creates a healthier environment for you and your family.`,
      features: ["Fabric & Leather Seat Cleaning", "Stain & Odor Removal", "Allergen Elimination (Dust Mites, Pollen)", "Sanitization Treatment", "Pet Hair & Dander Removal", "Quick Drying Technology", "Upholstery Protection Application", "Entire Vehicle Interior Available"],
      image: "https://images.unsplash.com/photo-1581548731548-c64695cc6952?q=80&w=2070&auto=format&fit=crop",
      color: "from-slate-500 to-gray-700",
      stats: { time: "1-2 hours", coverage: "All Seats", guarantee: "Freshness Guarantee", price: "Starting from $99" },
      popular: false,
      process: ["Vehicle Inspection & Assessment", "Deep Vacuuming & Debris Removal", "Pre-treatment of Stains & Spots", "Steam Cleaning & Sanitization", "Leather Conditioning (if applicable)", "Odor Elimination Treatment", "Rapid Airflow Drying", "Final Inspection & Grooming"],
      areasServed: ["All Melbourne Suburbs", "Dandenong", "Hallam", "Hampton Park", "Clayton", "Keysborough", "Glen Waverley", "Mount Waverley", "Noble Park", "Lynbrook", "Doveton", "Mornington Peninsula", "Frankston"],
      pricing: [
        { name: "2 Seats (Front)", price: "$99", details: "Driver + Passenger" },
        { name: "Full Set (5 Seats)", price: "$179", details: "Complete interior seats" },
        { name: "Leather Seats", price: "$149+", details: "Includes conditioning" },
        { name: "SUV/Large Vehicle", price: "$199", details: "Up to 7 seats" },
        { name: "Add-on: Carpet Cleaning", price: "$49", details: "Floor mats & carpets" },
      ],
      benefits: ["Healthier driving environment", "Remove stains & odors", "Eliminate allergens & bacteria", "Extend upholstery life", "Increase vehicle resale value", "Professional sanitization"],
      equipment: ["HEPA-filter vacuum systems", "Commercial steam cleaners", "Leather cleaning & conditioning products", "Stain extraction tools", "Air movers for drying", "Eco-friendly sanitizers"],
    },
  };

  const service = servicesData[serviceSlug];

  if (!service) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Service Not Found
          </h1>
          <p className="text-gray-600 mb-8">
            The service you're looking for doesn't exist.
          </p>
          <a
            href="/services"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Services
          </a>
        </div>
      </div>
    );
  }

  // Get the schema for this specific service
  const serviceSchema = getServiceSchema(serviceSlug, service);

  const faqs = [
    {
      q: `How long does ${service.title.toLowerCase()} take?`,
      a: service.stats.time === "Custom Hours" 
        ? "The duration varies based on the size of your property and specific requirements. We'll provide a time estimate after our initial assessment."
        : `Typically, ${service.title.toLowerCase()} takes ${service.stats.time}. However, this can vary based on property size and condition.`
    },
    {
      q: "Do I need to be home during the service?",
      a: "No, you don't need to be present. We can work with your schedule and access instructions. Many clients provide us with keys or access codes for convenience."
    },
    {
      q: "What preparation is needed before service?",
      a: "We recommend clearing surfaces and moving small items. For larger items, our team can assist. We'll discuss specific preparation needs during booking."
    },
    {
      q: "Are your cleaning products safe for children and pets?",
      a: "Yes, we use eco-friendly, non-toxic cleaning products that are safe for children, pets, and people with allergies. We can also use specific products if you have sensitivities."
    },
    {
      q: "Do you offer regular maintenance plans?",
      a: "Yes! We offer flexible maintenance plans including weekly, bi-weekly, or monthly service. Contact us for customized scheduling options."
    }
  ];

  // Add car-specific FAQs if this is the car seats cleaning service
  if (serviceSlug === "car-seats-cleaning") {
    faqs.unshift(
      {
        q: "How often should I get my car seats professionally cleaned?",
        a: "We recommend professional car seat cleaning every 6-12 months for regular use, or more frequently if you have children, pets, or frequently eat in your car. Regular cleaning helps maintain hygiene and extends upholstery life."
      },
      {
        q: "Can you remove tough stains like coffee, wine, or pet accidents?",
        a: "Yes! Our specialized stain removal treatments are designed to tackle even the toughest stains including coffee, wine, soda, grease, blood, and pet accidents. We use professional-grade products that are safe for your upholstery."
      },
      {
        q: "Is your car seat cleaning safe for leather seats?",
        a: "Absolutely. We use specialized leather cleaning and conditioning products that clean without damaging or drying out the leather. Our process removes dirt and grime while restoring natural oils to keep leather soft and supple."
      }
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Helmet>
        <title>{service.title} | Knight Cleanings</title>
        <meta name="description" content={`Professional ${service.title.toLowerCase()} services in Melbourne. ${service.description}`} />
        <meta name="keywords" content={`${service.title} Melbourne, ${service.title} Dandenong, Professional ${service.title.toLowerCase()} services, Car interior cleaning Melbourne, Auto upholstery cleaning`} />
        
        {/* Service-specific Schema */}
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
        
        {/* Additional LocalBusiness Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": "https://knightcleanings.com.au",
            "name": "Knight Cleanings",
            "url": "https://knightcleanings.com.au",
            "telephone": "+61420499661",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "11 Warath Drive, Dandenong North",
              "addressLocality": "Melbourne",
              "addressRegion": "VIC",
              "postalCode": "3175",
              "addressCountry": "AU"
            },
            "priceRange": "$$",
            "openingHours": "Mo-Su 07:00-19:00",
            "sameAs": [
              "https://www.facebook.com/knightcleanings",
              "https://www.instagram.com/knightcleanings"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative">
        <div className="h-96 bg-cover bg-center" style={{ backgroundImage: `url(${service.image})` }}>
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 -mt-48 relative z-10">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-2/3">
                <button
                  onClick={() => navigate('/services')}
                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-6"
                >
                  <ArrowLeft size={20} />
                  Back to All Services
                </button>
                
                <div className="flex items-center gap-4 mb-6">
                  <div className={`h-16 w-16 rounded-2xl bg-gradient-to-r ${service.color} flex items-center justify-center`}>
                    <service.icon size={28} className="text-white" />
                  </div>
                  <div>
                    <h1 className="text-4xl font-bold text-gray-900">
                      {service.title}
                    </h1>
                    <p className="text-gray-600 mt-2">{service.description}</p>
                  </div>
                </div>

                {service.popular && (
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-400 to-yellow-400 text-gray-900 font-bold px-4 py-2 rounded-full mb-6">
                    <Star size={16} />
                    <span>MOST POPULAR SERVICE</span>
                  </div>
                )}

                <div className="prose max-w-none mb-8">
                  <p className="text-gray-700 text-lg leading-relaxed">
                    {service.longDescription || service.description}
                  </p>
                </div>
              </div>

              <div className="lg:w-1/3">
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200 shadow-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">
                    Service Details
                  </h3>
                  
                  <div className="space-y-4 mb-8">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Clock className="text-blue-600" size={20} />
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Duration</div>
                        <div className="font-semibold text-gray-900">
                          {service.stats.time}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <Shield className="text-green-600" size={20} />
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Guarantee</div>
                        <div className="font-semibold text-gray-900">
                          {service.stats.guarantee}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                        <Users className="text-purple-600" size={20} />
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Team</div>
                        <div className="font-semibold text-gray-900">
                          Certified Professionals
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4 mb-6">
                    <div className="text-3xl font-bold text-gray-900 mb-1">
                      {service.stats.price}
                    </div>
                    <div className="text-sm text-gray-600">
                      *Final price depends on property size & condition
                    </div>
                  </div>

                  <button 
                    onClick={() => navigate(`/booking?service=${serviceSlug}`)}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-4 rounded-lg text-lg transition-all duration-300 hover:scale-[1.02] mb-4"
                  >
                    Book This Service
                  </button>
                  
                  <button 
                    onClick={() => window.open(`https://wa.me/61420499661?text=${encodeURIComponent(`I'm interested in ${service.title} service. Please send me a quote.`)}`, "_blank")}
                    className="w-full border-2 border-green-600 text-green-600 hover:bg-green-50 font-semibold py-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <MessageCircle size={20} />
                    <span>Get Instant Quote via WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Service Features
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {service.features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all hover:-translate-y-1"
              >
                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-blue-100 to-indigo-100 flex items-center justify-center mb-4">
                  <CheckCircle size={24} className="text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900">{feature}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Key Benefits
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {service.benefits?.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="h-16 w-16 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 mx-auto flex items-center justify-center text-white mb-4">
                  {index === 0 && <Heart size={24} />}
                  {index === 1 && <Leaf size={24} />}
                  {index === 2 && <Award size={24} />}
                  {index === 3 && <Clock size={24} />}
                  {index === 4 && <Target size={24} />}
                  {index === 5 && <ShieldCheck size={24} />}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {benefit}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Our Cleaning Process
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {service.process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className="h-20 w-20 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 mx-auto flex items-center justify-center text-white text-2xl font-bold">
                    {index + 1}
                  </div>
                  {index < service.process.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-blue-200 to-blue-100" />
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {step}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Pricing & Packages
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {service.pricing.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-xl transition-all hover:-translate-y-1"
              >
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {item.name}
                  </h3>
                  <div className="text-3xl font-bold text-gray-900 mb-3">
                    {item.price}
                  </div>
                  <p className="text-sm text-gray-600 mb-6">{item.details}</p>
                  <button 
                    onClick={() => navigate(`/booking?service=${serviceSlug}`)}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 rounded-lg transition-all duration-300 hover:scale-[1.02]"
                  >
                    Book Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment Section */}
      {service.equipment && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Professional Equipment Used
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {service.equipment.map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-4 border border-gray-200 flex items-center gap-4"
                >
                  <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    {index % 3 === 0 && <Truck size={24} className="text-blue-600" />}
                    {index % 3 === 1 && <RefreshCw size={24} className="text-blue-600" />}
                    {index % 3 === 2 && <Thermometer size={24} className="text-blue-600" />}
                  </div>
                  <span className="font-medium text-gray-900">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Areas Served */}
      <section className="py-16 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Areas We Serve
          </h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {service.areasServed.map((area, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-4 flex items-center gap-3 hover:shadow-md transition-shadow hover:border-blue-300 border border-transparent"
              >
                <MapPin size={20} className="text-blue-600 flex-shrink-0" />
                <span className="font-medium text-gray-900">{area}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-700 via-indigo-700 to-blue-800">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready for Professional {service.title}?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Book your service today and get 15% off your first booking!
            Limited time offer for new customers.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button 
              onClick={() => navigate(`/booking?service=${serviceSlug}`)}
              className="bg-gradient-to-r from-amber-400 to-yellow-400 hover:from-amber-500 hover:to-yellow-500 text-gray-900 font-bold px-8 py-4 rounded-full shadow-2xl hover:shadow-3xl hover:scale-[1.02] transition-all duration-300 text-lg flex items-center"
            >
              <Calendar size={20} className="mr-2" />
              Book Online Now
            </button>
            <button 
              onClick={() => window.open(`https://wa.me/61420499661?text=${encodeURIComponent(`I want to book ${service.title} service with 15% discount. Please help me.`)}`, "_blank")}
              className="border-2 border-white/30 hover:border-white hover:bg-white/10 backdrop-blur-sm font-semibold px-8 py-4 rounded-full transition-all duration-300 flex items-center"
            >
              <MessageCircle size={20} className="mr-2" />
              WhatsApp for Discount
            </button>
            <a
              href="tel:+61420499661"
              className="border-2 border-white/30 hover:border-white hover:bg-white/10 backdrop-blur-sm font-semibold px-8 py-4 rounded-full transition-all duration-300 flex items-center"
            >
              <Phone size={20} className="mr-2" />
              Call: +61 420 499 661
            </a>
          </div>
          <p className="text-blue-200 mt-6 text-sm">
            Offer valid for first-time customers only. Terms & conditions apply.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl p-6 border border-gray-200 cursor-pointer hover:border-blue-300 transition-colors"
                onClick={() => toggleFAQ(index)}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.q}</h3>
                    {expandedFAQ === index && (
                      <p className="text-gray-600">{faq.a}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0 ml-4">
                    {expandedFAQ === index ? (
                      <ChevronUp size={20} className="text-blue-600" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <div className="fixed bottom-6 right-6 z-40">
        <button
          onClick={() => window.open(`https://wa.me/61420499661?text=${encodeURIComponent(`I have a question about ${service.title} service.`)}`, "_blank")}
          className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-4 rounded-full shadow-2xl hover:shadow-3xl hover:scale-110 transition-all duration-300 animate-bounce-slow flex items-center justify-center"
        >
          <MessageCircle size={28} />
        </button>
      </div>
    </div>
  );
};

export default ServiceDetail;